import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from 'store';

import AppBar from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import Button from 'components/Buttons/Button';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import TravelerSelect from 'components/TravelerSelect';
import useIsDesktop from 'hooks/useIsDesktop';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { ProductType } from 'models/enums/productType';
import { amplitudelogEvent, TravelEditPaxEvent } from 'services/amplitudeService';
import { Loyalty } from 'services/loyaltiesService';
import { amplitudeEvents } from 'utils/amplitudeEvents';

import ProfileData from './components/ProfileData';
import ProfileEdit from './components/ProfileEdit';
import { Container, ButtonContainer } from './styles';
import useIsMobile from 'hooks/useIsMobile';


export type UserData = {
  userId: string;
  name: string;
  lastName: string;
  phoneNumber?: string;
  email: string;
  nationality?: string;
  gender?: 'Male' | 'Female';
  birthDate?: string | Date;
  birthDate_day?: string;
  birthDate_month?: string;
  birthDate_year?: string;
  documentNumber?: string;
  passportNumber?: string;
};

const USER_DATA_KEYS = [
  'name',
  'lastName',
  'phoneNumber',
  'email',
  'nationality',
  'gender',
  'birthDate',
  'documentNumber',
  'passportNumber',
];

type ConfirmProfileProps = {
  user: UserData;
  show: boolean;
  productType: ProductType;
  isInternationalFlight?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onSaveLoyaltyProgram?: () => void;
};

const ConfirmProfile = ({
  user,
  show,
  isInternationalFlight,
  productType,
  onClose,
  onConfirm,
  onSaveLoyaltyProgram,
}: ConfirmProfileProps) => {
  const { t } = useStoreTranslation();
  const [edit, setEdit] = useState(false);
  const isDesktop = useIsDesktop();
  const userAccount = useSelector((state: RootState) => state.user?.accountResult);
  const travelers = useSelector((state: RootState) => state.flights?.travelers.length > 0 ? state.flights?.travelers : state.hotels?.travelers);
  const [selectedLoyalty, setSelectedLoyalty] = useState<Loyalty>();
  const userLoyalties = useSelector((state: RootState) => state.userLoyalties.loyalties);
  const [profileUser, setProfileUser] = useState(user);
  const [travelerSelectKey, setTravelerSelectKey] = useState('travelerSelectKey');

  const isProfileComplete = useMemo(
    () =>
      profileUser &&
      Object.entries(profileUser).every(([key, value]) => {
        if (key === 'passportNumber') return !!value || !isInternationalFlight;
        if (key === 'birthDate') return !!value && value !== '0001-01-01T00:00:00Z';
        if (USER_DATA_KEYS.includes(key)) return !!value;
        return true;
      }),
    [profileUser, isInternationalFlight],
  );

  const isProfileCompleteForAllTravelers = () => {
    let complete = true;

    for (let index = 0; index < travelers?.length; index++) {
      const traveler = travelers[index];

      complete = isProfileCompleteForTraveler(traveler.id);

      if (complete === false) {
        return false;
      }
    }

    return true;
  };

  const isMobile = useIsMobile();

  const isProfileCompleteForTraveler = (id: string) => {
    let valid = true;
    const t = travelers.find((tra) => tra.id === id);
    valid = Object.entries(t).every(([key, value]) => {
      if (key === 'passportNumber') return !!value || !isInternationalFlight;
      if (key === 'birthDate') return !!value && value !== '0001-01-01T00:00:00Z';
      if (USER_DATA_KEYS.includes(key)) return !!value;
      return true;
    });

    if (!valid) {
      return false;
    }

    return valid;
  };

  useEffect(() => {
    if (userLoyalties && profileUser) {
      const selectedLoyalty = userLoyalties.find((l) => l.id === profileUser['id']);

      setSelectedLoyalty({
        loyaltyName: selectedLoyalty?.loyaltyName,
        loyaltyId: selectedLoyalty?.airlineCode,
        loyaltyNumber: selectedLoyalty?.loyaltyNumber,
      } as Loyalty);
    }
  }, [userLoyalties, profileUser]);

  useEffect(() => {
    setTravelerSelectKey(Math.random().toString());
    handleChangeTraveler(profileUser.userId || profileUser['id']);    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelers]);

  useEffect(() => {
    if (show) setEdit(false);
  }, [show]);

  const handleClickBack = () => {
    if (edit) setEdit(false);
    else onClose();
  };
  const handleClickEdit = () => {
    const event: TravelEditPaxEvent = {};

    amplitudelogEvent(amplitudeEvents.TRAVEL_EDIT_PAX, event, userAccount);

    setEdit(true);
  };

  const handleChangeTraveler = (id: string) => {
    setProfileUser(travelers?.find((t) => t.id === id || t.userId === id));
  };

  const handleSaveProfileEdit = () => {
    setEdit(false);
  };

  useEffect(() => {
    if (productType === ProductType.Hotel || productType === ProductType.Vehicle) {
      if (user !== undefined) {
        setProfileUser(user);
      }
    }
  }, []);

  return (
    <ApplicationOverlayContent show={show} width={1000} onClose={handleClickBack} testId="booking-confirm-profile">
      <Container>
        <Mobile>
          <AppBar
            goBack={handleClickBack}
            rightLabel={edit ? undefined : t('booking.confirmProfile.profileData.edit')}
            rightAction={handleClickEdit}
          >
            {t('booking.confirmProfile.profileData.title')}
          </AppBar>
        </Mobile>
        <Desktop>
          <ModalHeader onClose={onClose} title={t('booking.confirmProfile.profileData.title')} />
        </Desktop>
        {(productType === ProductType.Flight || productType === ProductType.Hotel) && travelers && travelers.length > 0 && !edit && (
          <TravelerSelect
            handleChangeTraveler={handleChangeTraveler}
            travelers={travelers.map((t) => ({
              fullName: t.name + ' ' + t.lastName + `${!isProfileCompleteForTraveler(t.id) ? ' (!)' : ''}`,
              id: t.id,
              userId: t.userId,
            }))}
            initialValue={profileUser.userId}
            key={travelerSelectKey}
          ></TravelerSelect>
        )}
        {edit ? (
          <ProfileEdit
            user={profileUser}
            productType={productType}
            isInternationalFlight={isInternationalFlight}
            onSave={handleSaveProfileEdit}
            loyaltyProgram={selectedLoyalty}
            onSaveLoyaltyProgram={onSaveLoyaltyProgram}
          />
        ) : (
          profileUser !== undefined && (
            <ProfileData
              user={profileUser}
              isInternationalFlight={isInternationalFlight}
              loyaltyProgram={selectedLoyalty}
              productType={productType}
            />
          )
        )}
        {!edit && (
          <ButtonContainer>
            {/* Si está con el perfil completo, puede editar */}
            {(productType !== ProductType.Flight && productType !== ProductType.Hotel) && isProfileComplete && (
              <>
                {isDesktop && (
                  <>
                    <Button onClick={handleClickEdit} invertedColor noBorder>
                      {t('booking.confirmProfile.profileData.edit')}
                    </Button>
                    <Button onClick={onConfirm}>{t('booking.confirmProfile.profileData.buttons.confirm')}</Button>
                  </>
                )}
                {isMobile && (
                  <>
                    <Button onClick={onConfirm}>{t('booking.confirmProfile.profileData.buttons.confirm')}</Button>
                  </>
                )}
              </>
            )}

            {(productType === ProductType.Flight || productType === ProductType.Hotel) && isProfileComplete && (
              <>
                {isDesktop && (
                  <>
                    <Button onClick={handleClickEdit} invertedColor noBorder>
                      {t('booking.confirmProfile.profileData.edit')}
                    </Button>
                  </>
                )}
              </>
            )}

            {/* Si es vuelos/hotel y tiene todos los perfiles completos, pueden confirmar */}
            {(productType === ProductType.Flight || productType === ProductType.Hotel) && isProfileCompleteForAllTravelers() && (
              <Button onClick={onConfirm}>{t('booking.confirmProfile.profileData.buttons.confirm')}</Button>
            )}

            {/* Si no está con el perfil completo, tiene que completar */}
            {!isProfileComplete && (
              <>
                {isDesktop && <div />}
                <Button onClick={handleClickEdit}>{t('booking.confirmProfile.profileData.buttons.complete')}</Button>
              </>
            )}
          </ButtonContainer>
        )}
      </Container>
    </ApplicationOverlayContent>
  );
};

export default ConfirmProfile;
